import React from 'react';
import { Modal, Form, Grid, Message, Divider, Button } from 'semantic';

import { useTranslation } from 'react-i18next';
import { useWizardContext } from '../context';
import { Trans } from 'react-i18next';
import { useUser } from 'contexts/user';
import { useAccount } from 'contexts/account';
import { Layout } from 'components/Layout';

import CountriesField from 'components/form-fields/Countries';
import PostalCode from 'components/form-fields/PostalCode';
import PriceField from 'components/form-fields/Price';
import MarketEnergyTariffsModal from 'components/MarketEnergyTariffs/Modal';
import { EnergyMarketTariffProvider } from 'components/MarketEnergyTariffs/Context';

/**
 *
 * @typedef {import("../../../../types/evse-invite").EvseInvite} EvseInvite
 * @typedef {import("../../../../types/location").Location} Location
 * @typedef {import("../../../../types/user").User} User
 * @typedef {import("../../../../types/evse-link").EvseLinkRequest} EvseLinkRequest
 **/

/**
 *
 * @param {Object} props
 * @param {function(): void} props.onBack
 * @param {EvseInvite} props.evseInvite
 * @param {string} props.stepHeader
 * @param {boolean} [props.isLast]
 * @param {(creditBilling: User['creditBilling'], evseLinkRequest: EvseLinkRequest, wantSticker: boolean, stationId: string) => Promise<void>} props.onSubmit
 * @returns {React.ReactElement}
 **/
export default function DetailsInvite({
  onBack,
  evseInvite,
  stepHeader,
  onSubmit,
  isLast = false,
}) {
  const { billingPlan, location, station, stickerCode, wantSticker } =
    useWizardContext();

  const { user } = useUser();
  const { account } = useAccount();
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);

  const [creditBilling, setCreditBilling] = React.useState({
    countryCode: 'nl',
    ...user?.creditBilling,
  });

  // this is a temporary way to handle required fields as we urgently needed a quickfix to ensure all fields are filled in
  // we should soon refactor all of this to use proper forms / validation
  const requiredFields = [
    'countryCode',
    'accountHolderName',
    'ibanNo',
    'address',
    'addressLine2',
    'postalCode',
    'city',
  ];

  const handleSubmit = () => {
    for (const field of requiredFields) {
      if (!creditBilling[field] || creditBilling[field] === '') {
        setError(new Error('Please fill in all required fields'));
        return false;
      }
    }

    setError(false);
    setLoading(true);

    const locationObj = {
      ...location,
      address: `${location.address} ${location.addressLine2}`,
    };
    delete locationObj.addressLine2;

    /** @type {EvseLinkRequest} */
    const body = {
      evseControllerId: station.id,
      billingPlanId: billingPlan.id,
      enablePublicCharging: true,
      enablePublicFreeCharging: false,
      location: {
        ...locationObj,
        accessPolicy: 'employeeReimburse',
        publishingMode: 'private',
      },
      evseInviteId: evseInvite.id,
      costSettings: station.connectors.map((c) => {
        return {
          connectorId: c.connectorId,
          pricePerKwh: Number(evseInvite.pricePerKwh),
        };
      }),
    };

    if (stickerCode) {
      body.evseId = stickerCode;
    } else {
      body.assignNewEvseId = 'true';
    }

    try {
      onSubmit(creditBilling, body, wantSticker, station.id);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
      return;
    }
  };

  return (
    <>
      <Modal.Header>
        {stepHeader}
        {t('newChargingStationDetails.header', 'Finalize')}
      </Modal.Header>

      <Modal.Content>
        <Form
          id="newChargingStationDetailsInvite"
          error={!!error}
          style={{ minHeight: '350px' }}
          onSubmit={handleSubmit}>
          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <Trans i18nKey="newChargingStationDetailsInvite.descriptionNew">
                  <b>How it works?</b>
                  <p>
                    The costs of billing plan will be paid by your employer.
                  </p>
                  <p>
                    The charge costs of charging the car at home will be
                    automatically reimbursed and paid by your employer.
                  </p>
                  <p>
                    At the beginning of the month you will receive a
                    self-billing invoice, the pay out (reimbursement) will be
                    made at the end of the month.
                  </p>
                </Trans>
                <Trans i18nKey="newChargingStationDetailsInvite.energyCostDescription">
                  <b>Energy Costs</b>
                  <p>
                    Energy costs to reimburse will be based on the energy market
                    prices combined with a surcharge per kWh to cover the
                    difference with your actual energy costs.
                  </p>
                  <p>
                    The Backup tariff will be used in case the current energy
                    market price is not available.
                  </p>
                </Trans>
                <Layout
                  horizontal
                  style={{
                    gap: '1em',
                  }}>
                  <Form.Field style={{ flex: 1 }}>
                    <label>{t('evseInviteModal.surcharge', 'Surcharge')}</label>
                    <PriceField
                      currency={account.billingCurrency}
                      value={evseInvite.surchargePerKwh || 0}
                      readOnly
                      style={{
                        opacity: 0.5,
                      }}
                    />
                  </Form.Field>
                  <Form.Field style={{ flex: 1 }}>
                    <label>
                      {t('evseInviteModal.backupTarriff', 'Backup tariff')}
                    </label>
                    <PriceField
                      currency={account.billingCurrency}
                      value={evseInvite.pricePerKwh}
                      readOnly
                      style={{
                        opacity: 0.5,
                      }}
                    />
                  </Form.Field>
                </Layout>
                {/*
                  Area hardcoded to NL for now. This is a product decision and it means that
                  NL dynamic pricing will be applied to stations that use this tariff profile
                  even if they are not located in NL.

                  This might be changed in the future making the area selectable.
                  */}
                <EnergyMarketTariffProvider energyDeliveryArea={'NL'}>
                  <MarketEnergyTariffsModal />
                </EnergyMarketTariffProvider>
              </Grid.Column>

              <Grid.Column width={8}>
                <div>
                  <b>
                    {t(
                      'newChargingStationDetails.reimbursementTitle',
                      'Reimbursement Details'
                    )}
                  </b>
                  <p>
                    {t(
                      'newChargingStationDetailsInvite.reimbursementText',
                      'Add your details to setup the reimbursement of your charging sessions by employer.'
                    )}
                  </p>
                </div>

                <Divider />

                {error && <Message error>{error.message}</Message>}

                <Form.Input
                  required={requiredFields.includes('accountHolderName')}
                  value={creditBilling.accountHolderName}
                  name="accountHolderName"
                  label={t(
                    'creditBilling.accountHolderName',
                    'Account Holder Name'
                  )}
                  type="text"
                  onChange={(e, { name, value }) =>
                    setCreditBilling({ ...creditBilling, [name]: value })
                  }
                />

                <Form.Input
                  required={requiredFields.includes('ibanNo')}
                  value={creditBilling?.ibanNo || ''}
                  name="ibanNo"
                  label={t('common.iban', 'IBAN Number')}
                  type="text"
                  onChange={(e, { value, name }) =>
                    setCreditBilling({ ...creditBilling, [name]: value })
                  }
                />

                <Form.Group widths="3">
                  <Form.Input
                    required={requiredFields.includes('address')}
                    width="12"
                    value={creditBilling.address || ''}
                    name="address"
                    label={t('formLabel.address', 'Address')}
                    type="text"
                    onChange={(e, { name, value }) =>
                      setCreditBilling({ ...creditBilling, [name]: value })
                    }
                  />

                  <Form.Input
                    required={requiredFields.includes('addressLine2')}
                    value={creditBilling.addressLine2 || ''}
                    name="addressLine2"
                    label={t('formLabel.addressLine2', 'No.')}
                    autoComplete="disabled"
                    type="text"
                    onChange={(e, { name, value }) =>
                      setCreditBilling({ ...creditBilling, [name]: value })
                    }
                  />
                </Form.Group>

                <CountriesField
                  required={requiredFields.includes('countryCode')}
                  name="countryCode"
                  label={t('formLabel.country', 'Country')}
                  value={creditBilling.countryCode || 'nl'}
                  onChange={(value) =>
                    setCreditBilling({ ...creditBilling, countryCode: value })
                  }
                />

                <Form.Group widths="equal">
                  <PostalCode
                    required={requiredFields.includes('postalCode')}
                    countryCode={creditBilling.countryCode || 'NL'}
                    value={creditBilling.postalCode || ''}
                    name="postalCode"
                    label={t('formLabel.postCode', 'Postal Code')}
                    type="text"
                    onChange={(e, { name, value }) =>
                      setCreditBilling({ ...creditBilling, [name]: value })
                    }
                  />

                  <Form.Input
                    required={requiredFields.includes('city')}
                    value={creditBilling.city || ''}
                    name="city"
                    label={t('formLabel.city', 'City/Town')}
                    type="text"
                    onChange={(e, { name, value }) =>
                      setCreditBilling({ ...creditBilling, [name]: value })
                    }
                  />
                </Form.Group>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          floated="right"
          loading={loading}
          disabled={loading}
          primary
          content={
            isLast ? t('common.create', 'Create') : t('common.next', 'Next')
          }
          form="newChargingStationDetailsInvite"
          style={{
            marginLeft: '1em',
            marginBottom: '1em',
          }}
        />
        <Button floated="right" basic onClick={() => onBack()}>
          {t('common.back', 'Back')}
        </Button>
      </Modal.Actions>
    </>
  );
}
